import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import {
  FilingFormState,
  SheetLevelStatusData,
} from './filing-form-state.service';
import { PureValidatorRegistry } from './pure-validator-registry.service';
import { SheetValidationContext } from './sheet-validator';
import { ConversionPhaseEnum } from '../../../types/commons';
import { SHEET_KEYS } from '../../models/sheet-keys';

/**
 * ParityCheckService
 *
 * Dev/staging safety net for Phase 2's per-sheet lifts. Whenever the editor
 * publishes a mount-based status for a sheet that ALSO has a registered
 * pure validator, this service runs the pure validator over the same data
 * and compares the {hasErrors, hasSuggestedChanges} pair. Disagreements
 * surface as a structured console.warn (non-blocking) so the dev sees the
 * divergence without interrupting the user workflow.
 *
 * Design notes
 * ------------
 *  - **Where it hooks**: FilingFormStateService.updateSheetLevelStatus calls
 *    `runIfPureValidatorRegistered(...)` after publishing the mount-based
 *    status. This is the one funnel every sheet's validation result flows
 *    through, so we get parity coverage automatically as the user exercises
 *    each sheet — no separate harness script required.
 *  - **What gets compared**: only the persisted display fields
 *    (`hasErrors`, `hasSuggestedChanges`). The pure validator's optional
 *    `errors` diagnostic is NOT compared since the mount-based path does
 *    not surface it consistently.
 *  - **Diagnostic surface on mismatch**: although `errors` is not used for
 *    comparison, when a parity mismatch fires we DO include the pure
 *    validator's per-field `errors` map (and its key list as
 *    `pureErrorFields`) in the structured console.warn payload, and inline
 *    the field list into the warn message string. This is the fastest way to
 *    point a dev at the specific qname(s) the pure validator considers
 *    invalid so they can locate the divergence against the imperative
 *    component. It is diagnostic only — it does not feed display or parity
 *    logic. The warn is non-blocking: no exception is thrown.
 *  - **Production behaviour**: bypass entirely. The lookup, logging, and
 *    pure-validator invocation all sit behind the `environment.production`
 *    guard so users pay zero cost.
 *  - **Lifecycle**: removed in Phase 2F cleanup once every sheet has shipped
 *    and parity has been observed across all 24 in dev + staging.
 */
@Injectable({ providedIn: 'root' })
export class ParityCheckService {
  private readonly registry = inject(PureValidatorRegistry);

  /**
   * Run the parity check for the given sheet's mount-based result. No-op in
   * production, no-op when no pure validator is registered for the sheet
   * (Phase 2 mid-rollout — most sheets haven't been lifted yet).
   *
   * @param sheetKey         - The sheet whose status was just published.
   * @param sheetData        - The data the editor used to compute its
   *                           mount-based status. Pass the latest snapshot
   *                           (e.g. via FilingFormStateService.getSheetData).
   * @param context          - Validation context (submissionType,
   *                           accountingStandard, optional crossSheetData).
   *                           Caller assembles the same shape the
   *                           orchestrator would use, so the pure validator
   *                           sees the same inputs in both places.
   * @param mountBasedResult - The {hasErrors, hasSuggestedChanges} pair the
   *                           editor just published.
   * @param conversionPhase  - Current conversion phase. Passed by the caller
   *                           (FilingFormStateService) rather than read from
   *                           the state service directly, because injecting
   *                           FilingFormStateService here would create a DI
   *                           cycle (state-service → parity-check →
   *                           state-service). `null` means the backend has
   *                           not yet reported the phase — treat as Phase 1.
   */
  runIfPureValidatorRegistered(
    sheetKey: keyof FilingFormState,
    sheetData: Record<string, any>,
    context: SheetValidationContext,
    mountBasedResult: SheetLevelStatusData,
    conversionPhase: ConversionPhaseEnum | null = null
  ): void {
    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log("[On runIfPureValidatorRegistered] START.");

    if (environment.production) return;

    // Phase gate: role1's filingInfo validator runs against user edits only
    // in Phase 2. In Phase 1 role1 is not yet revealed and the filingInfo
    // state slot is empty (PR-D strict phase-aware hydration writes only
    // the active source — filingPrerequisites for role0 in Phase 1). Running
    // role1's validator against empty data here would raise phantom parity
    // warnings the user cannot resolve. Skip until Phase 2 is active.
    if (
      sheetKey === SHEET_KEYS.filingInfo &&
      conversionPhase !== ConversionPhaseEnum.Phase2
    ) {
      return;
    }

    // Debug Log (Only enable when required to debug, do not allow this to land into prod)
    // console.log("[On Parity Check] Mounted Result: ", mountBasedResult);

    const validator = this.registry.get(sheetKey);
    if (!validator) return;

    let pureResult: SheetLevelStatusData;
    let pureErrors: { [field: string]: string } | undefined;
    try {
      const result = validator.validate(sheetData, context);
      
      // Debug Log (Only enable when required to debug, do not allow this to land into prod)
      // console.log("[On Parity Check] Pure Result: ", result);

      pureResult = {
        hasErrors: result.hasErrors,
        hasSuggestedChanges: result.hasSuggestedChanges,
      };
      // Capture the per-field error map for diagnostic logging on mismatch.
      // Not all validators populate `errors`; preserve undefined so we can
      // distinguish "validator omitted the map" from "validator reported
      // an empty map" downstream.
      pureErrors = result.errors;
    } catch (err) {
      // A throw inside the pure validator is itself a parity violation —
      // the mount-based path completed successfully for the same input.
      // Log the full error + stack for the dev, then return WITHOUT
      // re-throwing: this method is called from FilingFormStateService
      // .updateSheetLevelStatus, which is on the critical path of every
      // sheet's init/save flow (and, via markSheetInitializedWithStatus,
      // of sheet-init finalization). A rethrow here used to propagate out
      // of that call and abort markSheetInitializedWithStatus before it
      // could close the initializingSheets bracket — contradicting this
      // service's own "non-blocking" contract (see class docblock) and
      // permanently freezing the sheet's pencil flag. Never rethrow from
      // this method.
      console.error('[ParityCheck] pure validator threw during dual-path check', {
        sheetKey,
        tier: validator.tier,
        error: err,
      });
      return;
    }

    if (
      pureResult.hasErrors === mountBasedResult.hasErrors &&
      pureResult.hasSuggestedChanges === mountBasedResult.hasSuggestedChanges
    ) {
      return;
    }

    // Mismatch — log structured detail as a non-blocking console.warn.
    // The pure validator's per-field errors are the single most useful
    // hint when chasing a mismatch: they name the qname(s) the lifted
    // validator considers invalid, which the dev can then diff against
    // the imperative component's FormGroup error state. Surfaced in the
    // payload (full map) and inlined into the warn message (key list)
    // so it shows up in both the console object and the message string.
    const pureErrorFields = pureErrors ? Object.keys(pureErrors) : [];
    const payload: Record<string, unknown> = {
      sheetKey,
      tier: validator.tier,
      mountBased: mountBasedResult,
      pure: pureResult,
      pureErrorFields,
      submissionType: context.submissionType,
      accountingStandard: context.accountingStandard,
    };
    if (pureErrors) {
      payload['pureErrors'] = pureErrors;
    }
    console.warn(
      `[ParityCheck] sheet "${String(sheetKey)}" parity mismatch (non-blocking) — pure flagged: [${pureErrorFields.join(', ')}]`,
      payload
    );
  }
}
